import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { LiveDataBridge } from './data/LiveDataBridge.js';
import { AuthGate } from './session/AuthGate.js';
import { SessionProvider } from './session/SessionProvider.js';
import { StreamProvider } from './session/StreamProvider.js';
import { ChatView } from './views/ChatView.js';
import { WidgetView } from './views/WidgetView.js';
import { PaneView } from './views/PaneView.js';
import { WidgetPreview } from './widget/WidgetPreview.js';
import { initViewport } from './lib/viewport.js';
import './styles/tokens.css';
import './styles/globals.css';

// Publish `<html data-viewport>` before first paint — the widget stylesheets key
// off it instead of their own (iframe-sized) width. See lib/viewport.ts.
initViewport();

/**
 * Routes:
 *   /chat           — full-page chat (iframe embed)
 *   /widget         — floating launcher + panel (real Stream/bridge widget)
 *   /pane           — single-chat pane, no launcher (hosts' row chat)
 *   /widget-preview — bespoke widget UI on fixtures (temporary preview)
 *   /               — reserved for the future standalone app (redirects to /chat for now)
 *
 * SessionProvider wraps every route so both /chat and /widget redeem the handoff
 * and hold a session (no Stream cost); the seam gates on it as the re-wiring
 * epic lands. The fixtures preview simply never receives a handoff, so it stays
 * session-less and keeps rendering fixtures.
 */
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route
            path="/chat"
            element={
              <StreamProvider connect>
                <LiveDataBridge />
                <AuthGate>
                  <ChatView />
                </AuthGate>
              </StreamProvider>
            }
          />
          <Route path="/widget" element={<WidgetView />} />
          <Route path="/pane" element={<PaneView />} />
          <Route path="/widget-preview" element={<WidgetPreview />} />
          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  </StrictMode>,
);
